import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Table,
  LayerCard,
  Empty,
  Badge,
  Button,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { BuildingsIcon, HouseIcon, PlusIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Building, Room } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface BuildingWithCounts extends Building {
  companyCount: number
  roomCount: number
}

/* ------------------------------------------------------------------ */
/*  BuildingsPage                                                      */
/* ------------------------------------------------------------------ */

export function BuildingsPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toasts = useKumoToastManager()
  const [allBuildings, setBuildings] = useState<BuildingWithCounts[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Room management
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingWithCounts | null>(null)
  const [rooms, setRooms] = useState<Room[]>([])
  const [roomsLoading, setRoomsLoading] = useState(false)

  const loadBuildings = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const data = await apiRequest<BuildingWithCounts[]>('/api/buildings', { token })
      setBuildings(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void loadBuildings()
  }, [loadBuildings])

  async function loadRooms(buildingId: string) {
    setRoomsLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const data = await apiRequest<Room[]>(`/api/buildings/${buildingId}/rooms`, { token })
      setRooms(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setRoomsLoading(false)
    }
  }

  function openRoomManagement(building: BuildingWithCounts) {
    setSelectedBuilding(building)
    void loadRooms(building.id)
  }

  function closeRoomManagement() {
    setSelectedBuilding(null)
    setRooms([])
  }

  async function handleDeleteBuilding(building: BuildingWithCounts) {
    if (!window.confirm(t('buildings.confirmDelete'))) return
    try {
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/buildings/${building.id}`, { method: 'DELETE', token })
      toasts.add({ variant: 'success', title: t('buildings.deleteSuccess') })
      setLoading(true)
      await loadBuildings()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    }
  }

  async function handleDeleteRoom(room: Room) {
    if (!selectedBuilding) return
    if (!window.confirm(t('rooms.confirmDelete'))) return
    try {
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/buildings/${selectedBuilding.id}/rooms/${room.id}`, {
        method: 'DELETE',
        token,
      })
      toasts.add({ variant: 'success', title: t('rooms.deleteSuccess') })
      await loadRooms(selectedBuilding.id)
      setLoading(true)
      await loadBuildings()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    }
  }

  // Room management view
  if (selectedBuilding) {
    return (
      <div>
        <PageHeader
          breadcrumbs={
            <Breadcrumbs>
              <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
                {t('nav.home')}
              </Breadcrumbs.Link>
              <Breadcrumbs.Separator />
              <button
                type="button"
                onClick={closeRoomManagement}
                className="text-kumo-subtle hover:text-kumo-default transition-colors text-sm"
              >
                {t('buildings.title')}
              </button>
              <Breadcrumbs.Separator />
              <Breadcrumbs.Current>
                {selectedBuilding.name} - {t('rooms.title')}
              </Breadcrumbs.Current>
            </Breadcrumbs>
          }
          title={`${selectedBuilding.name} - ${t('rooms.title')}`}
          description={selectedBuilding.address}
        >
          <Button
            variant="primary"
            size="sm"
            icon={<PlusIcon size={16} />}
            onClick={() => navigate(`/buildings/${selectedBuilding.id}/rooms/new`)}
          >
            {t('rooms.addRoom')}
          </Button>
        </PageHeader>

        {roomsLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader size="lg" />
          </div>
        )}

        {!roomsLoading && rooms.length === 0 && (
          <Empty
            icon={<BuildingsIcon size={48} className="text-kumo-inactive" />}
            title={t('rooms.empty')}
          />
        )}

        {!roomsLoading && rooms.length > 0 && (
          <LayerCard>
            <LayerCard.Primary className="p-0">
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('rooms.name')}</Table.Head>
                    <Table.Head>{t('rooms.capacity')}</Table.Head>
                    <Table.Head>{t('rooms.type')}</Table.Head>
                    <Table.Head>{t('rooms.floor')}</Table.Head>
                    <Table.Head>{t('rooms.status')}</Table.Head>
                    <Table.Head>{t('rooms.actions')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rooms.map((room) => (
                    <Table.Row key={room.id}>
                      <Table.Cell>{room.name}</Table.Cell>
                      <Table.Cell>{room.capacity}</Table.Cell>
                      <Table.Cell>
                        <Badge variant="secondary">{t(`rooms.type.${room.type}`)}</Badge>
                      </Table.Cell>
                      <Table.Cell>{room.floor ?? '\u2014'}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          variant={
                            room.status === 'available'
                              ? 'primary'
                              : room.status === 'occupied'
                                ? 'secondary'
                                : 'secondary'
                          }
                        >
                          {t(`rooms.status.${room.status}`)}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PencilSimpleIcon size={16} />}
                            onClick={() =>
                              navigate(`/buildings/${selectedBuilding.id}/rooms/${room.id}/edit`)
                            }
                          >
                            {t('rooms.edit')}
                          </Button>
                          <Button
                            variant="secondary-destructive"
                            size="sm"
                            onClick={() => void handleDeleteRoom(room)}
                          >
                            {t('rooms.delete')}
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </LayerCard.Primary>
          </LayerCard>
        )}
      </div>
    )
  }

  // Buildings list view
  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('buildings.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('buildings.title')}
        description={t('buildings.description')}
      >
        <Button
          variant="primary"
          size="sm"
          icon={<PlusIcon size={16} />}
          onClick={() => navigate('/buildings/new')}
        >
          {t('buildings.addBuilding')}
        </Button>
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && allBuildings.length === 0 && (
        <Empty
          icon={<BuildingsIcon size={48} className="text-kumo-inactive" />}
          title={t('buildings.empty')}
        />
      )}

      {!loading && !error && allBuildings.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>{t('buildings.name')}</Table.Head>
                  <Table.Head>{t('buildings.city')}</Table.Head>
                  <Table.Head>{t('buildings.companyCount')}</Table.Head>
                  <Table.Head>{t('buildings.roomCount')}</Table.Head>
                  <Table.Head>{t('buildings.actions')}</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {allBuildings.map((building) => (
                  <Table.Row key={building.id}>
                    <Table.Cell>
                      <button
                        className="text-sm font-medium text-kumo-default hover:underline cursor-pointer bg-transparent border-none"
                        onClick={() => openRoomManagement(building)}
                      >
                        {building.name}
                      </button>
                    </Table.Cell>
                    <Table.Cell>{building.city}</Table.Cell>
                    <Table.Cell>{building.companyCount}</Table.Cell>
                    <Table.Cell>{building.roomCount}</Table.Cell>
                    <Table.Cell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<PencilSimpleIcon size={16} />}
                          onClick={() => navigate(`/buildings/${building.id}/edit`)}
                        >
                          {t('buildings.edit')}
                        </Button>
                        <Button
                          variant="secondary-destructive"
                          size="sm"
                          onClick={() => void handleDeleteBuilding(building)}
                        >
                          {t('buildings.delete')}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
